/**
 * BESZEDKAPU az atirat-felvetelhez: csak a SAJAT beszed kerul a felvetelbe.
 *
 * ─── Miert kell? ────────────────────────────────────────────────────────────
 *
 * Az elso eles meres (2026-09-23) ket dolgot mutatott:
 *
 *  - **Atszurodes.** Ket kolléga egy szobaban, fejhallgatoval: mindketto
 *    mikrofonja felvette a masikat is, halkan es torzan. Az atiratban ugyanaz a
 *    mondat ketszer szerepelt - egyszer jol ("Alphavet"), egyszer rosszul
 *    ("Alfalvet", "hónap reggel nyolcasként ... felfüccset").
 *  - **Csend.** A csendes, zajos szakaszokbol a modellek mondatokat talaltak
 *    ki - egy magyar hivasban eszt nyelven ("vähemalt tuleb oma noort...").
 *
 * Mindkettonek ugyanaz a gyogymodja: a felvetel csak akkor fusson, amikor a
 * mikrofon GAZDAJA beszel. Fejhallgato-mikrofonnal a sajat hang 15-20 dB-lel
 * hangosabb, mint a szomszed atszurodo hangja - ez a kulonbseg eleg a donteshez.
 *
 * ─── Hogyan dont? ───────────────────────────────────────────────────────────
 *
 * Blokkonkent (kb. 20 ms) a hangero (RMS, dBFS) alapjan, KET kuszobbel:
 *
 *  - **Zajszint felett** (`floor + 10 dB`): a csend es a zugas nem nyit.
 *  - **A sajat beszed alatt legfeljebb 14 dB-lel** (`speech - 14 dB`): a
 *    szomszed halk, atszurodo hangja nem nyit, ha mar hallottuk a gazdat.
 *
 * Mindket szint a mikrofonhoz IGAZODIK (halk es hangos kollégánal is), fix
 * kuszob nincs. A nyitas gyors (2 blokk), a zaras lassu (fel masodperc
 * utanfutas) - egy szo kozepen nem csuk be.
 *
 * Ez a fajl a TISZTA dontest tartalmazza (a szerver tesztjei ellenorzik); a
 * hangfeldolgozast a web `huddle/recorder.ts` epiti kore.
 */
export const DEFAULT_GATE = {
    aboveFloorDb: 10,
    belowSpeechDb: 14,
    openBlocks: 2,
    hangoverMs: 500,
    absoluteMinDb: -60,
};
export class VoiceGate {
    settings;
    /** A zajszint becslese (dBFS) - lassan emelkedik, gyorsan esik. */
    floor = -70;
    /**
     * A gazda beszedenek szintje (dBFS) - a hangos reszek mozgo atlaga. Kezdetben ovatosan alacsony: amig a gazdat nem hallottuk,
     * a kapu csak a zajszinthez igazodik.
     */
    speech = -45;
    loudStreak = 0;
    quietMs = 0;
    isOpen = false;
    constructor(settings = DEFAULT_GATE) {
        this.settings = settings;
    }
    get open() {
        return this.isOpen;
    }
    get threshold() {
        const s = this.settings;
        return Math.max(this.floor + s.aboveFloorDb, this.speech - s.belowSpeechDb, s.absoluteMinDb);
    }
    /** Egy blokk hangereje (dBFS) es hossza (ms). Visszaadja, ha a kapu allapota valtozott. */
    push(levelDb, blockMs) {
        const s = this.settings;
        const loud = levelDb > this.threshold;
        /*
          Zajszint: gyorsan kovet lefele, felfele lassan (kb. 1 dB/mp) - es CSAK
          csendes blokkbol. Kulonben egy hosszu, folyamatos beszed alatt maga a
          beszed emelne meg, es a kapu a mondat kozepen becsukna.
        */
        if (levelDb < this.floor)
            this.floor = levelDb;
        else if (!loud)
            this.floor += Math.min(levelDb - this.floor, blockMs / 1000);
        /*
          Beszedszint: a HANGOS blokkok mozgo atlaga - nem a csucs. Egy kohintes vagy
          egy koppanas a mikrofonon igy nem emeli percekre a kuszobot a gazda sajat
          beszede fole. Felfele gyorsabban koveti (uj, hangosabb beszelo), lefele
          lassan; csendben NAGYON lassan cseng le (kb. 1,2 dB/perc): hivasban a gazda
          percekig is hallgathat, es kozben a szomszed atszurodo hangja ne jusson at.
        */
        if (loud) {
            this.speech += (levelDb - this.speech) * (levelDb > this.speech ? 0.05 : 0.01);
        }
        else {
            this.speech -= (0.02 * blockMs) / 1000;
        }
        if (loud) {
            this.loudStreak += 1;
            this.quietMs = 0;
            if (!this.isOpen && this.loudStreak >= s.openBlocks) {
                this.isOpen = true;
                return 'open';
            }
            return null;
        }
        this.loudStreak = 0;
        if (!this.isOpen)
            return null;
        this.quietMs += blockMs;
        if (this.quietMs >= s.hangoverMs) {
            this.isOpen = false;
            this.quietMs = 0;
            return 'close';
        }
        return null;
    }
}
/** Egy hangblokk hangereje dBFS-ben (RMS). */
export function blockLevelDb(samples) {
    let sum = 0;
    for (const sample of samples)
        sum += sample * sample;
    const rms = Math.sqrt(sum / Math.max(1, samples.length));
    return 20 * Math.log10(rms || 1e-9);
}
//# sourceMappingURL=voice-gate.js.map